/**
 * Tiros.ts — Sprites efemeros de tiros (linha + flash no alvo), em blend aditivo.
 *
 * Cada tiro vive ~150ms, fade linear. Pool gerenciado por ticker no PixiApp.
 */

import { Graphics } from 'pixi.js';
import { corPlaneta } from '../config';
import type { TiroServidor, TiroVivo } from '../domain/tipos';
import type { PixiApp } from './PixiApp';

const TTL_LINHA_MS = 150;
const TTL_FLASH_MS = 100;

export class Tiros {
  private ativos: TiroVivo[] = [];

  constructor(private readonly pixi: PixiApp) {}

  iniciar(): void {
    this.pixi.addTicker((ticker) => this.atualizar(ticker.deltaMS));
  }

  criar(t: TiroServidor): void {
    const cor = corPlaneta(t.cor_dono);
    const torre = t.origem_tipo === 'torre';

    // Linha origem -> alvo (nucleo claro por cima da cor)
    const g = new Graphics();
    g.moveTo(t.ox, t.oy).lineTo(t.ax, t.ay)
      .stroke({ color: cor, width: torre ? 3.5 : 2, alpha: 0.9 });
    g.moveTo(t.ox, t.oy).lineTo(t.ax, t.ay)
      .stroke({ color: 0xffffff, width: torre ? 1.2 : 0.7, alpha: 0.7 });
    g.blendMode = 'add';
    this.pixi.tirosLayer.addChild(g);
    this.ativos.push({ sprite: g, ttlMs: TTL_LINHA_MS, totalTtlMs: TTL_LINHA_MS });

    // Flash no alvo
    const flash = new Graphics();
    flash.circle(t.ax, t.ay, torre ? 9 : 6).fill({ color: cor, alpha: 0.5 });
    flash.circle(t.ax, t.ay, torre ? 4 : 3).fill({ color: 0xffffff, alpha: 0.9 });
    flash.blendMode = 'add';
    this.pixi.tirosLayer.addChild(flash);
    this.ativos.push({ sprite: flash, ttlMs: TTL_FLASH_MS, totalTtlMs: TTL_FLASH_MS });
  }

  private atualizar(dtMs: number): void {
    for (let i = this.ativos.length - 1; i >= 0; i--) {
      const t = this.ativos[i];
      t.ttlMs -= dtMs;
      if (t.ttlMs <= 0) {
        if (t.sprite.parent) t.sprite.parent.removeChild(t.sprite);
        t.sprite.destroy();
        this.ativos.splice(i, 1);
      } else {
        t.sprite.alpha = Math.max(0, t.ttlMs / t.totalTtlMs);
      }
    }
  }
}
